import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Button, Form, Input, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { api, uploadDirectToBlob } from '../../utils/api';

const { TextArea } = Input;

const AdminAppEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  // In a real app, load app details by id. For now, keep editable states local.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch by id
  useEffect(() => {
    let isMounted = true;
    const fetchApp = async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}`);
        if (!isMounted) return;
        form.setFieldsValue({
          name: res.data.name,
          description: res.data.description,
          appInfo: res.data.appInfo,
        });
        setLogoUrl(res.data.logoUrl);
      } catch (e) {
        message.error('アプリ情報の取得に失敗しました。');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchApp();
    return () => {
      isMounted = false;
    };
  }, [form, id]);

  // Generate/revoke preview URL when selecting a new logo file
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoPreviewUrl('');
    }
  }, [logoFile]);

  const isValid = useMemo(() => {
    const values = form.getFieldsValue();
    const hasBasics = values?.name && values?.description && values?.appInfo;
    const hasLogo = logoFile || logoUrl; // require logo like create page
    return !!(hasBasics && hasLogo);
  }, [form, logoFile, logoUrl]);

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      // Upload logo on submit only if a new file is selected
      let finalLogoUrl = logoUrl;
      if (logoFile) {
        // derive folder prefix from app name
        const slug = String(values.name || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9\-]/g, '');
        const basePrefix = slug ? `admin-apps/${slug}` : 'admin-apps/unknown-app';
        const res = await uploadDirectToBlob(logoFile, `${basePrefix}/logo`);
        finalLogoUrl = res.url;
        setLogoUrl(finalLogoUrl);
      }
      await api.put(`/api/admin/apps/${id}`, { ...values, logoUrl: finalLogoUrl });
      message.success('アプリを更新しました。');
      navigate('/admin');
    } catch (e) {
      message.error('アプリ更新に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="text-xl font-semibold">アプリ情報の編集</h2>
      <Breadcrumb
        items={[
          { title: <Link to="/admin">アプリ一覧</Link> },
          { title: 'アプリ情報の編集' },
        ]}
      />

      <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish} className="mt-2">
        {/* 基本情報パネル */}
        <div className="bg-[#F4F6F9] rounded-lg p-4">
          <div className="text-base font-semibold mb-3 text-left">アプリの基本情報</div>
          {/* Row 1: 名前 + ロゴ */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Form.Item
              label={<span>名前 <span className="text-red-500">※</span></span>}
              name="name"
              rules={[{ required: true, message: '' }]}
            >
              <Input placeholder="アプリ名を入力" allowClear />
            </Form.Item>

            <Form.Item label={<span>ロゴ <span className="text-red-500">※</span></span>} required={false}>
              <div className="flex items-center gap-3">
                <Upload
                  beforeUpload={(file: File) => {
                    setLogoFile(file);
                    return false;
                  }}
                  maxCount={1}
                  showUploadList={logoFile ? { showPreviewIcon: false } : false}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />} className="w-[378px] h-[46px] rounded-[6px] border px-[120px]">
                    アップロード
                  </Button>
                </Upload>
                {logoPreviewUrl ? (
                  <img src={logoPreviewUrl} alt="new logo preview" className="h-[46px] w-auto object-contain rounded border border-gray-200 bg-white" />
                ) : (
                  logoUrl && (
                    <div className="flex items-center gap-2">
                      <img src={logoUrl} alt="current logo" className="h-[46px] w-auto object-contain" />
                    </div>
                  )
                )}
              </div>
            </Form.Item>
          </div>

          {/* Row 2: 説明 */}
          <Form.Item
            label={<span>説明 <span className="text-red-500">※</span></span>}
            name="description"
            rules={[{ required: true, message: '' }]}
          >
            <Input placeholder="アプリの説明を入力" allowClear />
          </Form.Item>

          {/* Row 3: アプリ情報 */}
          <Form.Item
            label={<span>アプリ情報 <span className="text-red-500">※</span></span>}
            name="appInfo"
            rules={[{ required: true, message: '' }]}
          >
            <TextArea rows={6} placeholder="アプリに関する詳細情報を入力" />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button onClick={() => navigate('/admin')} disabled={submitting}>キャンセル</Button>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={!isValid}>
            {submitting ? '保存中…' : '保存'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AdminAppEditPage;
